import produce from 'immer';

import {assert} from 'Asserts';
import {FieldDefinitionType} from 'graphql-client/__generated/graphqlGlobalTypes';
import deepCopy from 'lib/deepCopy';
import {
  ProcedureEdgeType,
  ProcedureFlowchart,
  ProcedureMaterialInputSetting,
  ProcedureMaterialOutputSetting,
} from 'procedures/ProcedureDomainTypes';
import {
  folderItemTypeToFieldDefinitionType,
  tagSchemaItemTypeToFieldDefinitionType,
} from 'procedures/shared-components/schema-field-type-selectors/materialTypeCategory';
import {BIOENTITY_LINK_FIELD_TYPE} from 'procedures/shared-components/schema-field-type-selectors/procedureSchemaFieldConstants';
import {
  FieldType,
  isRequiredLinkAnyEntity,
  isRequiredLinkCustomSchemaInterface,
  isRequiredLinkFolderItemType,
  isRequiredLinkTagSchema,
  RequiredLink,
} from 'schemas/fields/SchemaFieldConstants';

import {getEdgeTypeFromHandleId, getMaterialCidFromHandle} from './handleIdentifierUtils';

/**
 * Given a node CID and a handle ID, find the matching material output setting associated with that
 * handle, if one exists on the given node. This is meant to be used when initiating an edge.
 */
export function findMaterialOutputSetting(
  flowchart: ProcedureFlowchart,
  methodCid: string,
  handleId: string
): ProcedureMaterialOutputSetting | null {
  const edgeType = getEdgeTypeFromHandleId(handleId);
  if (edgeType !== ProcedureEdgeType.MATERIAL_EDGE) {
    return null;
  }
  const materialCid = getMaterialCidFromHandle(handleId);
  const methodNode = flowchart.nodes.find((node) => node.cid === methodCid);
  assert(methodNode != null, 'Expected a valid method node', {flowchart, methodCid});
  return (
    methodNode.materialOutputSettings.find((setting) => setting.materialOutputVersion.cid === materialCid) ||
    null
  );
}

/**
 * The "entity category" of a material constraint, used to decide UI edge compatibility.
 *
 * A material input/output can be constrained four ways, each producing a different category shape:
 *   - *any entity* (unconstrained) — `{kind: 'any'}`; compatible with everything (except text).
 *   - *specific schema* — `{kind: 'schema', schemaId}`; only an exact id match is compatible.
 *   - *entity-type* ("Any DNA Sequence") and *fieldset* — both reduce to an entity category via
 *     their `folderItemType` / `itemType`, represented as `{kind: 'category', category}` where
 *     `category` is a `FieldDefinitionType` (e.g. DNA_SEQUENCE_LINK). A fieldset with no base
 *     bioentity type has no category and behaves like "any entity".
 *
 * The UI is intentionally permissive (e.g. fieldset → specific-schema of the same category is allowed);
 * the stricter broad→narrow rejection happens in the workflow-validation layer.
 */
type MaterialCategory =
  | {kind: 'any'}
  | {kind: 'schema'; schemaId: string}
  | {kind: 'category'; category: FieldDefinitionType};

function getMaterialCategory(requiredLink: RequiredLink | null | undefined): MaterialCategory {
  if (isRequiredLinkTagSchema(requiredLink)) {
    return {kind: 'schema', schemaId: requiredLink.tagSchema.id};
  }
  if (isRequiredLinkFolderItemType(requiredLink)) {
    return {kind: 'category', category: folderItemTypeToFieldDefinitionType(requiredLink.folderItemType)};
  }
  if (isRequiredLinkCustomSchemaInterface(requiredLink)) {
    const {itemType} = requiredLink.schemaInterface;
    // A fieldset that declares no base bioentity type can hold any entity, so treat it as unconstrained.
    return itemType == null
      ? {kind: 'any'}
      : {kind: 'category', category: tagSchemaItemTypeToFieldDefinitionType(itemType)};
  }
  // Any entity (or any other unconstrained link).
  return {kind: 'any'};
}

export function isMaterialConnectionCompatible(
  materialOutputSetting: ProcedureMaterialOutputSetting | null,
  materialInputSetting: ProcedureMaterialInputSetting
): boolean {
  // It's fine to invoke this function without an outputSetting
  if (materialOutputSetting == null) {
    return false;
  }

  const inputDataType = materialInputSetting.materialInputVersion.dataType;
  const outputDataType = materialOutputSetting.materialOutputVersion.dataType;

  // Any constraint -> text: never compatible.
  if (inputDataType.type !== FieldType.BIOENTITY_LINK) {
    return false;
  }

  const inputCategory = getMaterialCategory(inputDataType.requiredLink);
  const outputCategory = getMaterialCategory(outputDataType.requiredLink);

  // Any constraint -> "any entity" input: always compatible.
  if (inputCategory.kind === 'any') {
    return true;
  }

  // "any entity" output -> constrained input: the source could resolve to a non-matching entity, so
  // the UI rejects it (the user must pick a compatible output type).
  if (outputCategory.kind === 'any') {
    return false;
  }

  // Specific schema on both sides: only an exact id match is compatible.
  if (inputCategory.kind === 'schema' && outputCategory.kind === 'schema') {
    return inputCategory.schemaId === outputCategory.schemaId;
  }

  // Mixed schema/category, or category/category: compare entity categories. This is where the UI is
  // intentionally permissive — e.g. a fieldset or entity-type output connects to a same-category
  // specific-schema input even though the workflow layer is stricter.
  const inputCategoryType =
    inputCategory.kind === 'schema' ? schemaCategory(inputDataType.requiredLink) : inputCategory.category;
  const outputCategoryType =
    outputCategory.kind === 'schema' ? schemaCategory(outputDataType.requiredLink) : outputCategory.category;

  return inputCategoryType === outputCategoryType;
}

/**
 * The entity category of a specific-schema link, derived from its `folderItemType`. Returns null when
 * the schema link carries no folderItemType (so it won't spuriously match another category).
 */
function schemaCategory(requiredLink: RequiredLink | null | undefined): FieldDefinitionType | null {
  if (!isRequiredLinkTagSchema(requiredLink)) {
    return null;
  }
  const {folderItemType} = requiredLink.tagSchema;
  return folderItemType != null ? folderItemTypeToFieldDefinitionType(folderItemType) : null;
}

// We create a new, modified inputSetting which is compatible with the given output setting.
// In practice: the output materials TYPE is copied into the input material, and any planned
// values are removed. Note that this function does not _assess_ compatibility.
export function makeCompatibleMaterialInputSetting(
  materialOutputSetting: ProcedureMaterialOutputSetting,
  initialMaterialInputSetting: ProcedureMaterialInputSetting
): ProcedureMaterialInputSetting {
  return produce(initialMaterialInputSetting, (draftInputSetting) => {
    draftInputSetting.materialInputVersion.dataType = deepCopy(
      materialOutputSetting.materialOutputVersion.dataType
    );

    // If the input type was "any entity", then it is already compatible and we reset it to the generic
    // bioentity link. A constrained input (specific schema, entity-type, or fieldset) keeps its type, so
    // we only reset when the input was genuinely unconstrained.
    if (
      initialMaterialInputSetting.materialInputVersion.dataType.type === FieldType.BIOENTITY_LINK &&
      isRequiredLinkAnyEntity(initialMaterialInputSetting.materialInputVersion.dataType.requiredLink)
    ) {
      draftInputSetting.materialInputVersion.dataType = BIOENTITY_LINK_FIELD_TYPE;
    }

    // We remove any linked materials, without touching the associated amounts
    for (const value of draftInputSetting.values) {
      value.material = {
        type: FieldType.BIOENTITY_LINK,
        value: '',
        targetFiles: [],
      };
    }
  });
}
